import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useGetList,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { marketsForScope } from '../dataProvider/types';
import type { BlogCategory, BlogPost } from '../dataProvider';

/**
 * Blog posts list (D-cms-blog). The CmsPageList shape PLUS the category and author
 * columns. OPERATIONAL market-scoped: the switcher governs which posts show (the active
 * markets PLUS the all-market posts, the RuleMarket idiom). ROLE-gated to editor and
 * super_admin (a capability read-gate would leak via a viewer's '*:read'). Authoring a
 * new post gates cms:author; publishing is taken from the edit view. The categoryKey
 * resolves to the category label via the blogCategories vocabulary.
 */

const blogFilters = [
  <TextInput key="q" source="q" label="Search title" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'draft', name: 'Draft' },
      { id: 'published', name: 'Published' },
    ]}
  />,
];

const BlogPostListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton label="New post" />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const BlogPostListView = () => {
  const { activeRegion } = useRegion();
  // The active markets PLUS the all-market posts (a both post shows in every scope).
  const markets = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  // The category vocabulary, to resolve a post's categoryKey to its display label.
  const { data: categories } = useGetList<BlogCategory>('blogCategories', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'label', order: 'ASC' },
  });
  const labelFor = (key: string) =>
    categories?.find((c) => c.key === key)?.label ?? key;
  return (
    <List
      resource="blogPosts"
      title="Blog posts"
      filter={{ market: markets }}
      filters={blogFilters}
      actions={<BlogPostListActions />}
      sort={{ field: 'title', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="title" label="Title" />
        <FunctionField
          label="Category"
          render={(r: BlogPost) => labelFor(r.categoryKey)}
        />
        <FunctionField
          label="Market"
          render={(r: BlogPost) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Status"
          render={(r: BlogPost) => (
            <Chip
              size="small"
              variant={r.status === 'published' ? 'filled' : 'outlined'}
              color={r.status === 'published' ? 'success' : 'default'}
              label={r.status === 'published' ? 'Published' : 'Draft'}
              data-testid={`blog-status-${r.id}`}
            />
          )}
        />
        <FunctionField
          label="Published"
          render={(r: BlogPost) =>
            r.publishedAt?.slice(0, 10) ?? 'not published'
          }
        />
        <TextField source="author" label="Author" />
      </Datagrid>
    </List>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const BlogPostList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <BlogPostListView />
  </RequireAccess>
);
